/**
 * .Bank Cumulative Report
 *
 * A month-by-month grid of .bank statistics (metrics x years), matching the
 * historical "Bank Stats - Cumulative" workbook. Current-period values are
 * computed live from monitor/contact/deal data; every cell is editable so
 * historical data (pre-2025) can be entered or corrected by hand.
 *
 * Persistence lives in the Convex `bankStats` table, one row per cell. A
 * Convex cron (see convex/bankStatsSnapshot.ts) captures the computed values
 * on the last day of each month, so "Capture Snapshot" is only needed for an
 * early or corrected snapshot. Metric math is shared with the cron via
 * convex/bankStatsCompute.ts.
 */

import { Fragment, useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react';
import { format, startOfMonth, startOfYear } from 'date-fns';
import {
  Camera,
  FileSpreadsheet,
  Loader2,
  Pencil,
  Plus,
  Save,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { read as readWorkbook, utils, write as writeWorkbook } from 'xlsx';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useReportBuilderContactEmails } from '@/hooks/use-contacts';
import { useDeals } from '@/hooks/use-deals';
import { getRegion } from '@/lib/region-utils';
import { downloadXlsx, styleXlsxCells } from '@/lib/sheetjs-patches';
import { cn } from '@/lib/utils';
import type { LiveDomainRow } from '../../../../convex/bankStatsCompute';
import { api } from '../../../../convex/_generated/api';
import {
  computeLiveStats,
  isEffectiveLive,
  resolveEffectiveStatus,
} from '../../../../convex/bankStatsCompute';

const REPORT_PATH = '/app/reports/bank-cumulative';

// ============================================================================
// Metric Configuration
// ============================================================================

interface MetricDef {
  key: string;
  label: string; // UI row label
  exportLabel: string; // workbook row label
  computable: boolean;
  hint?: string;
}

interface MetricGroup {
  label: string;
  metrics: MetricDef[];
}

const METRIC_GROUPS: MetricGroup[] = [
  {
    label: 'New Domains',
    metrics: [
      { key: 'newDomainsMonth', label: 'New Domains (for the month)', exportLabel: 'New domains*', computable: true, hint: 'Registrations dated within the month, per fTLD tracking' },
      { key: 'newDomainsYtd', label: 'New Domains (cumulative for the year)', exportLabel: 'New domains (cumulative for the year)*', computable: true },
      { key: 'newDomainsNewRegistrants', label: 'New Domains — New Registrants', exportLabel: 'New domains - New Registrants*', computable: true, hint: 'Registrant email\'s first .bank domain was registered this year (rough estimate)' },
      { key: 'newDomainsExistingRegistrants', label: 'New Domains — Existing Registrants', exportLabel: 'New domains - Existing Registrants*', computable: true },
    ],
  },
  {
    label: 'Activations (Live)',
    metrics: [
      { key: 'newActivationsMonth', label: 'New Activations (for the month)', exportLabel: 'New Activations (for the month = raw)', computable: true, hint: 'Domains whose go-live date falls within the month' },
      { key: 'newActivationsYtd', label: 'New Activations (cumulative for the year)', exportLabel: 'New Activations (cumulative for the year)', computable: true },
      { key: 'avgMonthlyNewActivations', label: 'Average Monthly New Activations', exportLabel: 'Average Monthly New Activations', computable: true },
      { key: 'activationsUs', label: 'Activations — U.S.', exportLabel: 'Activations - U.S.', computable: true, hint: 'Currently-live domains with a US-region registrant (unknown region counts as US)' },
      { key: 'activationsIntl', label: 'Activations — International', exportLabel: 'Activations - International', computable: true },
      { key: 'activationsTotal', label: 'Total Activations', exportLabel: 'Total Activations', computable: true },
    ],
  },
  {
    label: 'Registrants & Domains Under Management',
    metrics: [
      { key: 'dum', label: 'Domains Under Management', exportLabel: 'Domains Under Management', computable: true, hint: 'Active (not deleted) .bank domains as of the report date' },
      { key: 'uniqueRegistrants', label: 'Unique Registrants (Total)', exportLabel: 'Unique Registrants (Total)', computable: true, hint: 'Distinct registrant emails — acknowledged rough estimate' },
      { key: 'uniqueRegistrantsUs', label: 'Unique Registrants — U.S.', exportLabel: 'Unique Registrants - U.S.', computable: true },
      { key: 'uniqueRegistrantsIntl', label: 'Unique Registrants — Int.', exportLabel: 'Unique Registrants - Int.', computable: true },
    ],
  },
  {
    label: 'Applications (New Business Deals)',
    metrics: [
      { key: 'applicationsMonth', label: 'Applications (for the month)', exportLabel: 'Applications (for the month)', computable: true, hint: 'New Business deals with a .bank domain created within the month' },
      { key: 'applicationsYtd', label: 'Applications (cumulative for the year)', exportLabel: 'Applications (cumulative for the year)', computable: true },
    ],
  },
  {
    label: 'Rates',
    metrics: [
      { key: 'approvalRate', label: 'Applications Approval Rate', exportLabel: 'Applications Approval Rate (YTD for current year)', computable: true, hint: 'Approved / decided New Business .bank deals. Previous years hold the whole-year total; current year is YTD.' },
      { key: 'renewalRate', label: 'Renewal Rate', exportLabel: 'Renewal Rate', computable: false, hint: 'Manually entered' },
    ],
  },
];

const ALL_METRICS: MetricDef[] = METRIC_GROUPS.flatMap((g) => g.metrics);

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_SHEET_NAMES = [
  'Jan.',
  'Feb.',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Ordered matchers for importing workbook row labels (specific before generic)
const IMPORT_MATCHERS: Array<{ test: (label: string) => boolean; key: string }> = [
  { test: (l) => l.startsWith('new domains - new registrants'), key: 'newDomainsNewRegistrants' },
  { test: (l) => l.startsWith('new domains - existing'), key: 'newDomainsExistingRegistrants' },
  { test: (l) => l.startsWith('new domains (cumulative'), key: 'newDomainsYtd' },
  { test: (l) => l.startsWith('new domains'), key: 'newDomainsMonth' },
  { test: (l) => l.startsWith('new activations (for the month'), key: 'newActivationsMonth' },
  { test: (l) => l.startsWith('new activations (cumulative'), key: 'newActivationsYtd' },
  { test: (l) => l.startsWith('average monthly new activations'), key: 'avgMonthlyNewActivations' },
  { test: (l) => l.startsWith('activations - u.s'), key: 'activationsUs' },
  { test: (l) => l.startsWith('activations - int'), key: 'activationsIntl' },
  { test: (l) => l.startsWith('total activations'), key: 'activationsTotal' },
  { test: (l) => l.startsWith('domains under management'), key: 'dum' },
  { test: (l) => l.startsWith('unique registrants - u.s'), key: 'uniqueRegistrantsUs' },
  { test: (l) => l.startsWith('unique registrants - int'), key: 'uniqueRegistrantsIntl' },
  { test: (l) => l.startsWith('unique registrants'), key: 'uniqueRegistrants' },
  { test: (l) => l.startsWith('applications (for the month'), key: 'applicationsMonth' },
  { test: (l) => l.startsWith('applications (cumulative'), key: 'applicationsYtd' },
  { test: (l) => l.startsWith('applications approval rate'), key: 'approvalRate' },
  { test: (l) => l.startsWith('renewal rate'), key: 'renewalRate' },
];

function sheetNameToMonth(name: string): number | null {
  const prefixes = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const lower = name.trim().toLowerCase();
  const idx = prefixes.findIndex((p) => lower.startsWith(p));
  return idx === -1 ? null : idx + 1;
}

// ============================================================================
// Types
// ============================================================================

interface StatCell {
  year: number;
  month: number;
  metric: string;
  value: string;
  source: 'manual' | 'computed' | 'imported';
  updatedAt: number;
}

const SOURCE_STYLES: Record<StatCell['source'], { dot: string; label: string }> = {
  manual: { dot: 'bg-amber-500', label: 'Manually entered' },
  computed: { dot: 'bg-blue-500', label: 'Captured from live data' },
  imported: { dot: 'bg-slate-400', label: 'Imported from workbook' },
};

// ============================================================================
// Workbook Import / Export
// ============================================================================

interface ImportedCell {
  year: number;
  month: number;
  metric: string;
  value: string;
}

function parseWorkbook(data: ArrayBuffer): { cells: ImportedCell[]; sheets: number } {
  const wb = readWorkbook(data, { type: 'array' });
  const cells: ImportedCell[] = [];
  let sheets = 0;

  for (const sheetName of wb.SheetNames) {
    const month = sheetNameToMonth(sheetName);
    const ws = wb.Sheets[sheetName];
    if (!month || !ws || !ws['!ref'])
      continue;

    const rows = utils.sheet_to_json<Array<string | undefined>>(ws, {
      header: 1,
      raw: false,
      blankrows: false,
    });

    // Find the header row: first row whose second cell parses as a year
    const isYear = (val: unknown) => {
      const n = Number(String(val ?? '').trim());
      return Number.isInteger(n) && n >= 1990 && n <= 2100;
    };
    const headerIdx = rows.findIndex((r) => isYear(r[1]));
    if (headerIdx === -1)
      continue;

    const years: number[] = [];
    for (let c = 1; c < rows[headerIdx].length; c++) {
      if (isYear(rows[headerIdx][c]))
        years.push(Number(rows[headerIdx][c]));
      else break;
    }
    if (years.length === 0)
      continue;
    sheets++;

    const noteLines: string[] = [];
    for (const [r, row] of rows.entries()) {
      if (r === headerIdx)
        continue;
      const label = String(row[0] ?? '').trim();
      if (!label)
        continue;

      const lower = label.toLowerCase();
      const matcher = IMPORT_MATCHERS.find((m) => m.test(lower));
      if (matcher) {
        for (const [i, year] of years.entries()) {
          const value = String(row[i + 1] ?? '').trim();
          if (value) {
            cells.push({ year, month, metric: matcher.key, value });
          }
        }
      } else {
        noteLines.push(label);
      }
    }

    if (noteLines.length > 0) {
      cells.push({ year: 0, month, metric: 'notes', value: noteLines.join('\n') });
    }
  }

  return { cells, sheets };
}

// ============================================================================
// Editable Cell Component
// ============================================================================

function EditableCell({
  saved,
  liveValue,
  onSave,
  onClear,
}: {
  saved: StatCell | undefined;
  liveValue: string | undefined;
  onSave: (value: string) => void;
  onClear: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(saved?.value ?? liveValue ?? '');
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed === (saved?.value ?? ''))
      return;
    if (!trimmed && saved) {
      onClear();
      return;
    }
    if (trimmed)
      onSave(trimmed);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter')
              handleSave();
            else if (e.key === 'Escape')
              setIsEditing(false);
          }}
          className="h-6 w-20 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>
    );
  }

  const display = saved?.value ?? liveValue;
  const sourceStyle = saved ? SOURCE_STYLES[saved.source] : null;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={startEditing}
            className="group flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm tabular-nums hover:bg-muted/60"
          >
            {sourceStyle && <span className={cn('size-1.5 shrink-0 rounded-full', sourceStyle.dot)} />}
            {display === undefined
              ? (
                  <span className="text-muted-foreground/40">—</span>
                )
              : (
                  <span className={cn(!saved && 'italic text-muted-foreground')}>{display}</span>
                )}
            <Pencil className="ml-auto size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-60" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {saved
            ? (
                <>
                  <div className="font-medium">{sourceStyle?.label}</div>
                  <div className="text-muted-foreground">{format(new Date(saved.updatedAt), 'MMM d, yyyy h:mm a')}</div>
                  {liveValue !== undefined && liveValue !== saved.value && (
                    <div className="mt-1">
                      Live value:
                      {' '}
                      <span className="font-mono">{liveValue}</span>
                    </div>
                  )}
                </>
              )
            : (display === undefined
                ? (
                    <div>No data — click to enter</div>
                  )
                : (
                    <div>Live computed value — not yet saved</div>
                  ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function BankCumulativeReport() {
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [extraYears, setExtraYears] = useState<number[]>([]);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setLabel, removeLabel } = useBreadcrumb();

  useEffect(() => {
    setLabel(REPORT_PATH, { name: '.Bank Cumulative Report', path: REPORT_PATH });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  // --- Saved stats from Convex ---
  const savedStats = useQuery(api.bankStats.listAll) as StatCell[] | undefined;
  const upsertCell = useMutation(api.bankStats.upsertCell);
  const clearCell = useMutation(api.bankStats.clearCell);
  const bulkUpsert = useMutation(api.bankStats.bulkUpsert);

  // --- Live data sources (same universe the Report Builder loads) ---
  const cutoffMs = useMemo(() => Date.now() - 3 * 24 * 60 * 60 * 1000, []);
  const {
    results: domainStatuses,
    status: domainStatusesLoadStatus,
    loadMore: loadMoreDomainStatuses,
  } = usePaginatedQuery(
    api.monitor.getActiveDomainStatusesPaginated,
    { cutoffMs },
    { initialNumItems: 1000 },
  );
  useEffect(() => {
    if (domainStatusesLoadStatus === 'CanLoadMore') {
      loadMoreDomainStatuses(1000);
    }
  }, [domainStatusesLoadStatus, loadMoreDomainStatuses]);

  const overrides = useQuery(api.monitor.getAllActiveOverrides);

  const bankDomains = useMemo(
    () => [...new Set((domainStatuses || [])
      .filter((d) => d.domain.toLowerCase().endsWith('.bank'))
      .map((d) => d.domain.toLowerCase()))].sort((a, b) => a.localeCompare(b)),
    [domainStatuses],
  );
  const domainsLoaded = domainStatusesLoadStatus === 'Exhausted';
  const { data: contactEmailsByDomain } = useReportBuilderContactEmails(
    bankDomains,
    domainsLoaded && bankDomains.length > 0,
  );
  const { data: deals } = useDeals();

  // --- Build live domain rows (.bank only) ---
  const liveDomainRows = useMemo((): LiveDomainRow[] => {
    if (!domainStatuses)
      return [];

    const overrideMap = new Map<string, string>();
    if (overrides) {
      for (const o of overrides) overrideMap.set(o.domain, o.statusOverride);
    }

    const contactMap = new Map<string, { registrantEmail: string | null; registrantCountry: string | null }>();
    if (contactEmailsByDomain) {
      for (const row of contactEmailsByDomain) {
        contactMap.set(row.domain.toLowerCase(), {
          registrantEmail: row.registrantEmail,
          registrantCountry: row.registrantCountry,
        });
      }
    }

    const nowMs = Date.now();
    return domainStatuses
      .filter((d) => d.domain.toLowerCase().endsWith('.bank'))
      .map((d) => {
        const effectiveStatus = resolveEffectiveStatus(d, overrideMap.get(d.domain), nowMs);
        const contact = contactMap.get(d.domain.toLowerCase());
        return {
          domain: d.domain,
          effectiveLive: isEffectiveLive(effectiveStatus),
          registrationDate: d.registrationDate,
          liveSince: d.liveSince ?? null,
          registrantEmail: contact?.registrantEmail ?? null,
          registrantRegion: getRegion(contact?.registrantCountry ?? null),
        };
      });
  }, [domainStatuses, overrides, contactEmailsByDomain]);

  const liveDataReady = domainsLoaded && !!contactEmailsByDomain && !!deals;
  const liveStats = useMemo(
    () => (liveDataReady
      ? computeLiveStats(liveDomainRows, deals, {
          month: currentMonth,
          monthStart: startOfMonth(now).getTime(),
          yearStart: startOfYear(now).getTime(),
          end: Number.POSITIVE_INFINITY,
        })
      : {}),
    [liveDataReady, liveDomainRows, deals, now, currentMonth],
  );

  // --- Grid data ---
  const savedMap = useMemo(() => {
    const map = new Map<string, StatCell>();
    for (const cell of savedStats ?? []) {
      map.set(`${cell.year}|${cell.month}|${cell.metric}`, cell);
    }
    return map;
  }, [savedStats]);

  const years = useMemo(() => {
    const set = new Set<number>([currentYear, ...extraYears]);
    for (const cell of savedStats ?? []) {
      if (cell.year > 0)
        set.add(cell.year);
    }
    return [...set].sort((a, b) => a - b);
  }, [savedStats, currentYear, extraYears]);

  const monthNotes = savedMap.get(`0|${selectedMonth}|notes`);

  const isCurrentPeriod = useCallback(
    (year: number) => year === currentYear && selectedMonth === currentMonth,
    [currentYear, selectedMonth, currentMonth],
  );

  // --- Handlers ---
  const handleSaveCell = useCallback(async (year: number, metric: string, value: string) => {
    try {
      await upsertCell({ year, month: selectedMonth, metric, value, source: 'manual' });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save value');
    }
  }, [upsertCell, selectedMonth]);

  const handleClearCell = useCallback(async (year: number, metric: string) => {
    try {
      await clearCell({ year, month: selectedMonth, metric });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clear value');
    }
  }, [clearCell, selectedMonth]);

  const handleSaveNotes = useCallback(async () => {
    if (notesDraft === null)
      return;
    try {
      await upsertCell({ year: 0, month: selectedMonth, metric: 'notes', value: notesDraft, source: 'manual' });
      setNotesDraft(null);
      toast.success('Notes saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save notes');
    }
  }, [upsertCell, selectedMonth, notesDraft]);

  // Capture the live computed values for the current period as a snapshot
  const handleCaptureSnapshot = useCallback(async () => {
    if (!liveDataReady) {
      toast.error('Live data is still loading');
      return;
    }
    setIsCapturing(true);
    try {
      const cells = ALL_METRICS
        .filter((m) => m.computable && liveStats[m.key] !== undefined)
        .map((m) => ({
          year: currentYear,
          month: currentMonth,
          metric: m.key,
          value: liveStats[m.key],
        }));
      const result = await bulkUpsert({ cells, source: 'computed', skipManual: true });
      toast.success(`Snapshot captured for ${MONTH_NAMES[currentMonth - 1]} ${currentYear} (${result.written} values${result.skipped ? `, ${result.skipped} manual kept` : ''})`);
      setSelectedMonth(currentMonth);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to capture snapshot');
    } finally {
      setIsCapturing(false);
    }
  }, [liveDataReady, liveStats, bulkUpsert, currentYear, currentMonth]);

  // Import a historical workbook (seeds/overwrites imported+computed cells; manual kept)
  const handleImportFile = useCallback(async (file: File) => {
    setIsImporting(true);
    try {
      const data = await file.arrayBuffer();
      const { cells, sheets } = parseWorkbook(data);
      if (cells.length === 0) {
        toast.error('No recognizable data found in the workbook');
        return;
      }
      // Chunk to keep individual mutations small
      let written = 0;
      let skipped = 0;
      for (let i = 0; i < cells.length; i += 250) {
        const result = await bulkUpsert({
          cells: cells.slice(i, i + 250),
          source: 'imported',
          skipManual: true,
        });
        written += result.written;
        skipped += result.skipped;
      }
      toast.success(`Imported ${written} values from ${sheets} sheets${skipped ? ` (${skipped} manual values kept)` : ''}`);
    } catch (error) {
      console.error('Workbook import failed:', error);
      toast.error('Failed to import workbook');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current)
        fileInputRef.current.value = '';
    }
  }, [bulkUpsert]);

  // Export all 12 months to a workbook matching the original layout
  const handleExport = useCallback(async () => {
    try {
      const wb = utils.book_new();

      for (let month = 1; month <= 12; month++) {
        const rows: Array<Array<string | number>> = [];
        rows.push([`Figures thru ${MONTH_NAMES[month - 1]} unless noted otherwise`, ...years]);

        for (const group of METRIC_GROUPS) {
          for (const metric of group.metrics) {
            const row: Array<string | number> = [metric.exportLabel];
            for (const year of years) {
              const saved = savedMap.get(`${year}|${month}|${metric.key}`);
              const live = (year === currentYear && month === currentMonth) ? liveStats[metric.key] : undefined;
              row.push(saved?.value ?? live ?? '');
            }
            rows.push(row);
          }
        }

        const notes = savedMap.get(`0|${month}|notes`);
        if (notes?.value) {
          rows.push(['']);
          for (const line of notes.value.split('\n')) {
            rows.push([line]);
          }
        }

        const ws = utils.aoa_to_sheet(rows);
        ws['!cols'] = [{ wch: 48 }, ...years.map(() => ({ wch: 12 }))];
        utils.book_append_sheet(wb, ws, MONTH_SHEET_NAMES[month - 1]);
      }

      const filename = `bank_stats_cumulative_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
      // SheetJS CE can't write cell styles, so patch the generated file directly:
      // bold column A (labels/notes), center the year columns.
      const raw: ArrayBuffer = writeWorkbook(wb, { type: 'array', bookType: 'xlsx' });
      const styled = await styleXlsxCells(new Uint8Array(raw), {
        cellStyle: (col) => {
          if (col === 0)
            return { bold: true };
          if (col <= years.length)
            return { center: true };
        },
      });
      downloadXlsx(styled, filename);
      toast.success(`Exported workbook to ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export workbook');
    }
  }, [years, savedMap, liveStats, currentYear, currentMonth]);

  const handleAddYear = useCallback(() => {
    const minYear = years[0] ?? currentYear;
    setExtraYears((prev) => [...prev, minYear - 1]);
  }, [years, currentYear]);

  const isLoading = savedStats === undefined;

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">.Bank Cumulative Report</h1>
          <p className="text-sm text-muted-foreground">
            Monthly .bank statistics by year. Current-period values are computed live; every cell is editable for historical data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file)
                void handleImportFile(file);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="gap-2"
          >
            {isImporting ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            Import Workbook
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isLoading}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export Workbook
          </Button>
          <Button
            size="sm"
            onClick={handleCaptureSnapshot}
            disabled={!liveDataReady || isCapturing}
            className="gap-2"
          >
            {isCapturing ? <Loader2 className="size-4 animate-spin" /> : <Camera className="size-4" />}
            Capture Snapshot
          </Button>
        </div>
      </div>

      {/* Month selector */}
      <Card className="mb-4 border-dashed">
        <CardContent className="py-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Figures Thru
              </span>
              <div className="flex flex-wrap gap-1.5">
                {MONTH_NAMES.map((name, idx) => {
                  const month = idx + 1;
                  const isSelected = selectedMonth === month;
                  const isCurrent = month === currentMonth;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setSelectedMonth(month)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                      )}
                    >
                      {name}
                      {isCurrent && <span className="opacity-70">•</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-amber-500" />
                  Manual
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-blue-500" />
                  Snapshot
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-slate-400" />
                  Imported
                </span>
                <span className="italic">Italic = live (unsaved)</span>
                <span>Snapshots are captured automatically on the last day of each month (manual values are kept)</span>
              </div>
              {!liveDataReady && (
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Loading live data...
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats grid */}
      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading saved statistics...</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <Card>
              <CardContent className="pt-4">
                <Table className="border border-border">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-80 border border-border">
                        Figures thru
                        {' '}
                        {MONTH_NAMES[selectedMonth - 1]}
                      </TableHead>
                      {years.map((year) => (
                        <TableHead key={year} className={cn('w-28 border border-border', year === currentYear && 'font-bold text-foreground')}>
                          {year}
                          {year === currentYear && selectedMonth === currentMonth && (
                            <span className="ml-1.5 text-[10px] font-normal uppercase text-muted-foreground">live</span>
                          )}
                        </TableHead>
                      ))}
                      <TableHead className="w-24 border border-border">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddYear}
                          className="h-6 gap-1 px-2 text-xs text-muted-foreground"
                        >
                          <Plus className="size-3" />
                          {(years[0] ?? currentYear) - 1}
                        </Button>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {METRIC_GROUPS.map((group) => (
                      <Fragment key={group.label}>
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={years.length + 2} className="border border-border bg-muted/40 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {group.label}
                          </TableCell>
                        </TableRow>
                        {group.metrics.map((metric) => (
                          <TableRow key={metric.key}>
                            <TableCell className="border border-border py-1.5 text-sm">
                              {metric.hint
                                ? (
                                    <TooltipProvider>
                                      <Tooltip delayDuration={300}>
                                        <TooltipTrigger asChild>
                                          <span className="cursor-default underline decoration-muted-foreground/40 decoration-dotted underline-offset-4">
                                            {metric.label}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-72 text-xs">
                                          {metric.hint}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )
                                : (
                                    metric.label
                                  )}
                            </TableCell>
                            {years.map((year) => (
                              <TableCell key={year} className="border border-border py-1.5">
                                <EditableCell
                                  saved={savedMap.get(`${year}|${selectedMonth}|${metric.key}`)}
                                  liveValue={
                                    isCurrentPeriod(year) && metric.computable
                                      ? liveStats[metric.key]
                                      : undefined
                                  }
                                  onSave={(value) => void handleSaveCell(year, metric.key, value)}
                                  onClear={() => void handleClearCell(year, metric.key)}
                                />
                              </TableCell>
                            ))}
                            <TableCell className="border border-border" />
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

      {/* Month notes */}
      {!isLoading && (
        <Card className="mt-4">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">
              Notes for
              {' '}
              {MONTH_NAMES[selectedMonth - 1]}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 pt-0">
            <Textarea
              value={notesDraft ?? monthNotes?.value ?? ''}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Footnotes for this month's sheet (e.g. corrections, estimates, methodology notes)..."
              rows={4}
              className="text-sm"
            />
            {notesDraft !== null && notesDraft !== (monthNotes?.value ?? '') && (
              <div className="flex items-center gap-2 self-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNotesDraft(null)}
                  className="gap-1.5 text-xs"
                >
                  <X className="size-3" />
                  Discard
                </Button>
                <Button size="sm" onClick={() => void handleSaveNotes()} className="gap-1.5 text-xs">
                  <Save className="size-3" />
                  Save Notes
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default BankCumulativeReport;
